import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BrandFormData } from "../hooks/useBrandForm";
import { LogoUpload } from "./LogoUpload";
import { BrandColorPicker } from "./BrandColorPicker";
import { ChipInput } from "./ChipInput";

interface BrandDetailsFormProps {
  formData: BrandFormData;
  errors: Record<string, string>;
  onFieldChange: (field: keyof BrandFormData, value: any) => void;
  customerId: string;
}

const CTA_OPTIONS = [
  { value: "Call Us", label: "Call Us" },
  { value: "Book Now", label: "Book Now" },
  { value: "Get a Quote", label: "Get a Quote" },
  { value: "Learn More", label: "Learn More" },
  { value: "Get a Free Estimate", label: "Get a Free Estimate" },
  { value: "Visit Website", label: "Visit Website" },
  { value: "Message Us", label: "Message Us" },
];

export const BrandDetailsForm: React.FC<BrandDetailsFormProps> = ({
  formData,
  errors,
  onFieldChange,
  customerId,
}) => {
  const taglineCharCount = formData.tagline.length;
  const taglineMaxChars = 120;

  // State for accordion sections - default all open
  const [expandedSections, setExpandedSections] = React.useState<Set<string>>(
    new Set(["core", "aesthetics", "guidelines"])
  );

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  // Calculate form completion progress
  const calculateProgress = () => {
    const requiredFields = [
      formData.brandName.length >= 2,
      formData.businessType !== "",
      formData.businessType === "SERVICE" ? formData.servicesOffered.length > 0 : true,
      formData.businessType === "PRODUCT" ? formData.productsOffered.length > 0 : true,
      formData.businessType === "BOTH" ? (formData.servicesOffered.length > 0 || formData.productsOffered.length > 0) : true,
      formData.website.trim().length > 0, // Website is required for AI agent
      formData.serviceArea.length > 0, // Service Area is now required
      formData.brandVoice.trim().length >= 10, // Brand Voice is now required (min 10 chars)
    ];
    const completed = requiredFields.filter(Boolean).length;
    return Math.round((completed / requiredFields.length) * 100);
  };

  // Validation helpers
  const isFieldValid = (fieldName: keyof BrandFormData) => {
    const value = formData[fieldName];
    if (typeof value === 'string' && value.trim().length > 0 && !errors[fieldName]) {
      return true;
    }
    return false;
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="mb-6">
        <h2 className="mb-1 text-xl font-semibold text-brand-primary">
          Brand Details
        </h2>
        <p className="text-sm text-zinc-600 dark:text-theme-fg-muted">
          Manage your brand identity & assets.
        </p>
      </div>

      {/* Progress Indicator */}
      <div className="mb-4 rounded-lg border border-zinc-200/90 dark:border-theme-border bg-white dark:bg-theme-surface p-4 shadow-sm ring-1 ring-zinc-200/60 dark:ring-theme-border/50">
        <div className="mb-2 flex justify-between text-sm">
          <span className="font-medium text-zinc-700 dark:text-theme-fg">Form Completion</span>
          <span className="font-semibold text-brand-primary-light">
            {calculateProgress()}%
          </span>
        </div>
        <div className="h-2 w-full rounded-full bg-surface-ui">
          <div
            className="h-2 rounded-full bg-brand-primary-light transition-all duration-300"
            style={{ width: `${calculateProgress()}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-zinc-500 dark:text-theme-fg-muted">
          <span className="text-red-500">*</span> indicates required field
        </p>
      </div>

      {/* Error Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 shadow-sm ring-1 ring-red-100">
          <div className="flex items-start gap-2">
            <span className="material-icons text-red-500 text-xl">error_outline</span>
            <div className="flex-1">
              <h4 className="font-semibold text-red-800 mb-2 text-sm">
                Please fix the following errors:
              </h4>
              <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                {Object.entries(errors).map(([field, error]) => (
                  <li key={field}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Core Brand Info Section */}
      <div className="overflow-hidden rounded-lg border border-zinc-200/90 dark:border-theme-border bg-white dark:bg-theme-surface shadow-sm ring-1 ring-zinc-200/60 dark:ring-theme-border/50">
        <button
          type="button"
          onClick={() => toggleSection("core")}
          className="flex w-full items-center justify-between p-3 transition-colors hover:bg-zinc-50/80 dark:hover:bg-white/5"
        >
          <h3 className="text-base font-semibold text-brand-primary">
            Core Brand Info
          </h3>
          <motion.span
            className="material-icons text-xl text-zinc-500 dark:text-theme-fg-muted"
            animate={{ rotate: expandedSections.has("core") ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            expand_more
          </motion.span>
        </button>

        <AnimatePresence>
          {expandedSections.has("core") && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="space-y-4 border-t border-zinc-100 dark:border-theme-border p-4 pt-0">
                {/* Brand Name */}
                <div>
                  <label className="block text-sm font-medium text-[#2f4941] dark:text-theme-fg mb-1.5 flex justify-between items-center">
                    <span>
                      Brand Name <span className="text-red-500">*</span>
                    </span>
                    <span className="text-xs text-gray-500 dark:text-theme-fg-muted font-normal">
                      {formData.brandName.length}/50
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.brandName}
                      onChange={(e) => onFieldChange("brandName", e.target.value)}
                      maxLength={50}
                      className={`w-full px-3 py-2 pr-10 text-sm border rounded-lg focus:outline-none focus:ring-2 transition ${
                        errors.brandName
                          ? "border-red-500 focus:ring-red-200"
                          : formData.brandName.length >= 2
                          ? "border-green-500 focus:ring-green-200"
                          : "border-gray-300 focus:ring-[#2f4941]/30 focus:border-[#2f4941]"
                      }`}
                      placeholder="Acme Corporation"
                    />
                    {/* Success icon when valid */}
                    {formData.brandName.length >= 2 && !errors.brandName && (
                      <span className="absolute right-3 top-2.5 text-green-500 material-icons text-sm">
                        check_circle
                      </span>
                    )}
                    {/* Error icon when invalid */}
                    {errors.brandName && (
                      <span className="absolute right-3 top-2.5 text-red-500 material-icons text-sm">
                        error
                      </span>
                    )}
                  </div>
                  {errors.brandName ? (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <span className="material-icons text-xs">info</span>
                      {errors.brandName}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500 dark:text-theme-fg-muted">
                      Minimum 2 characters required
                    </p>
                  )}
                </div>

                {/* Business Type */}
                <div>
                  <label className="block text-sm font-medium text-[#2f4941] dark:text-theme-fg mb-1.5">
                    Business Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.businessType}
                    onChange={(e) => onFieldChange("businessType", e.target.value)}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 transition bg-white dark:bg-theme-surface dark:text-theme-fg ${
                      errors.businessType
                        ? "border-red-500 focus:ring-red-200"
                        : "border-gray-300 dark:border-theme-border focus:ring-[#2f4941]/30 focus:border-[#2f4941]"
                    }`}
                  >
                    <option value="">Select business type</option>
                    <option value="SERVICE">Service</option>
                    <option value="PRODUCT">Product</option>
                    <option value="BOTH">Both</option>
                  </select>
                  {errors.businessType && (
                    <p className="mt-1 text-xs text-red-600">
                      {errors.businessType}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500 dark:text-theme-fg-muted">
                    Are you primarily a service business, a product business, or both?
                  </p>
                </div>

                {/* Industry */}
                <div>
                  <label className="block text-sm font-medium text-[#2f4941] dark:text-theme-fg mb-1.5">
                    Industry <span className="text-gray-400 text-xs">(recommended)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.industry}
                    onChange={(e) => onFieldChange("industry", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f4941]/30 focus:border-[#2f4941] transition"
                    placeholder="e.g., Carpet Cleaning, Med Spa, Roofing, SaaS"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-theme-fg-muted">
                    Help us understand your industry for better content generation.
                  </p>
                </div>

                {/* Website URL */}
                <div>
                  <label className="block text-sm font-medium text-[#2f4941] dark:text-theme-fg mb-1.5 flex items-center gap-1">
                    Website URL <span className="text-theme-fg-muted font-normal">(optional)</span>
                    <span 
                      className="material-icons text-gray-400 text-sm cursor-help" 
                      title="Enter your full website URL (e.g., https://example.com or www.example.com)"
                    >
                      info
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.website}
                      onChange={(e) => onFieldChange("website", e.target.value)}
                      className={`w-full px-3 py-2 pr-10 text-sm border rounded-lg focus:outline-none focus:ring-2 transition ${
                        errors.website
                          ? "border-red-500 focus:ring-red-200"
                          : isFieldValid("website")
                          ? "border-green-500 focus:ring-green-200"
                          : "border-gray-300 focus:ring-[#2f4941]/30 focus:border-[#2f4941]"
                      }`}
                      placeholder="https://www.acmecorp.com or www.acmecorp.com"
                    />
                    {/* Success icon when valid */}
                    {isFieldValid("website") && (
                      <span className="absolute right-3 top-2.5 text-green-500 material-icons text-sm">
                        check_circle
                      </span>
                    )}
                    {/* Error icon when invalid */}
                    {errors.website && (
                      <span className="absolute right-3 top-2.5 text-red-500 material-icons text-sm">
                        error
                      </span>
                    )}
                  </div>
                  {errors.website ? (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <span className="material-icons text-xs">info</span>
                      {errors.website}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500 dark:text-theme-fg-muted">
                      Enter your full URL with https:// or just the domain name
                    </p>
                  )}
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-[#2f4941] dark:text-theme-fg mb-1.5 flex items-center gap-1">
                    Phone Number
                    <span className="text-gray-400 text-xs ml-1">(optional)</span>
                    <span 
                      className="material-icons text-gray-400 text-sm cursor-help" 
                      title="10-15 digits. Any format accepted: (555) 123-4567 or +1-555-123-4567"
                    >
                      info
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) =>
                        onFieldChange("phoneNumber", e.target.value)
                      }
                      className={`w-full px-3 py-2 pr-10 text-sm border rounded-lg focus:outline-none focus:ring-2 transition ${
                        errors.phoneNumber
                          ? "border-red-500 focus:ring-red-200"
                          : isFieldValid("phoneNumber")
                          ? "border-green-500 focus:ring-green-200"
                          : "border-gray-300 focus:ring-[#2f4941]/30 focus:border-[#2f4941]"
                      }`}
                      placeholder="+1 (555) 123-4567"
                    />
                    {/* Success icon when valid */}
                    {isFieldValid("phoneNumber") && (
                      <span className="absolute right-3 top-2.5 text-green-500 material-icons text-sm">
                        check_circle
                      </span>
                    )}
                    {/* Error icon when invalid */}
                    {errors.phoneNumber && (
                      <span className="absolute right-3 top-2.5 text-red-500 material-icons text-sm">
                        error
                      </span>
                    )}
                  </div>
                  {errors.phoneNumber ? (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <span className="material-icons text-xs">info</span>
                      {errors.phoneNumber}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500 dark:text-theme-fg-muted flex items-center gap-1">
                      <span className="material-icons text-xs">format_list_numbered</span>
                      Accepted formats: (555) 123-4567, 555-123-4567, or +1-555-123-4567
                    </p>
                  )}
                </div>

                {/* Service Area */}
                <div>
                <ChipInput
                    label="Service Area *"
                  chips={formData.serviceArea}
                  onChipsChange={(areas) => onFieldChange("serviceArea", areas)}
                  placeholder="Type a city/area and press Enter"
                  helperText="Add multiple cities or regions (e.g., Seattle, Bellevue)"
                />
                  {errors.serviceArea && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <span className="material-icons text-xs">error</span>
                      {errors.serviceArea}
                    </p>
                  )}
                </div>

                {/* Conditional Requirement Hint */}
                {formData.businessType === "SERVICE" && (
                  <div className="p-3 bg-[#fffbeb] dark:bg-accent-warm/10 border-l-4 border-[#e4b372] dark:border-accent-warm/50 text-sm text-[#92400e] dark:text-[#b8832a] rounded flex items-start gap-2">
                    <span className="material-icons text-[#e4b372] dark:text-accent-warm text-lg">info</span>
                    <span>Since you selected "Service", please add at least one service below.</span>
                  </div>
                )}
                {formData.businessType === "PRODUCT" && (
                  <div className="p-3 bg-[#fffbeb] dark:bg-accent-warm/10 border-l-4 border-[#e4b372] dark:border-accent-warm/50 text-sm text-[#92400e] dark:text-[#b8832a] rounded flex items-start gap-2">
                    <span className="material-icons text-[#e4b372] dark:text-accent-warm text-lg">info</span>
                    <span>Since you selected "Product", please add at least one product below.</span>
                  </div>
                )}
                {formData.businessType === "BOTH" && (
                  <div className="p-3 bg-[#fffbeb] dark:bg-accent-warm/10 border-l-4 border-[#e4b372] dark:border-accent-warm/50 text-sm text-[#92400e] dark:text-[#b8832a] rounded flex items-start gap-2">
                    <span className="material-icons text-[#e4b372] dark:text-accent-warm text-lg">info</span>
                    <span>Since you selected "Both", please add at least one service or product below.</span>
                  </div>
                )}

                {/* Services Offered - show if SERVICE or BOTH */}
                {(formData.businessType === "SERVICE" || formData.businessType === "BOTH" || !formData.businessType) && (
                  <div>
                    <ChipInput
                      label={
                        formData.businessType === "SERVICE" || formData.businessType === "BOTH"
                          ? "Services Offered *"
                          : "Services Offered"
                      }
                      chips={formData.servicesOffered}
                      onChipsChange={(services) =>
                        onFieldChange("servicesOffered", services)
                      }
                      placeholder="Type a service and press Enter"
                      helperText="Add individual services (e.g., Consulting, SEO)"
                    />
                    {errors.servicesOffered && (
                      <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                        <span className="material-icons text-xs">error</span>
                        {errors.servicesOffered}
                      </p>
                    )}
                  </div>
                )}

                {/* Products Offered - show if PRODUCT or BOTH */}
                {(formData.businessType === "PRODUCT" || formData.businessType === "BOTH") && (
                  <div>
                    <ChipInput
                      label={
                        formData.businessType === "PRODUCT" || formData.businessType === "BOTH"
                          ? "Products Offered *"
                          : "Products Offered"
                      }
                      chips={formData.productsOffered}
                      onChipsChange={(products) =>
                        onFieldChange("productsOffered", products)
                      }
                      placeholder="Type a product and press Enter"
                      helperText="Add individual products you sell"
                    />
                    {errors.productsOffered && (
                      <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                        <span className="material-icons text-xs">error</span>
                        {errors.productsOffered}
                      </p>
                    )}
                  </div>
                )}

                {/* Tagline */}
                <div>
                  <label className="block text-sm font-medium text-[#2f4941] dark:text-theme-fg mb-1.5">
                    Tagline <span className="text-gray-400 text-xs ml-1">(optional)</span>
                  </label>
                  <textarea
                    value={formData.tagline}
                    onChange={(e) => onFieldChange("tagline", e.target.value)}
                    rows={2}
                    maxLength={taglineMaxChars}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 transition resize-none ${
                      errors.tagline
                        ? "border-red-500 focus:ring-red-200"
                        : "border-gray-300 focus:ring-[#2f4941]/30 focus:border-[#2f4941]"
                    }`}
                    placeholder="Innovating for a brighter tomorrow."
                  />
                  <div className="mt-1 flex items-center justify-between">
                    <p className="text-xs text-gray-500 dark:text-theme-fg-muted">
                      A short, memorable phrase.
                    </p>
                    <span className="text-xs text-gray-500 dark:text-theme-fg-muted">
                      {taglineCharCount}/{taglineMaxChars}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Brand Aesthetics Section */}
      <div className="overflow-hidden rounded-lg border border-zinc-200/90 dark:border-theme-border bg-white dark:bg-theme-surface shadow-sm ring-1 ring-zinc-200/60 dark:ring-theme-border/50">
        <button
          type="button"
          onClick={() => toggleSection("aesthetics")}
          className="flex w-full items-center justify-between p-3 transition-colors hover:bg-zinc-50/80 dark:hover:bg-white/5"
        >
          <h3 className="text-base font-semibold text-brand-primary">
            Brand Aesthetics
          </h3>
          <motion.span
            className="material-icons text-xl text-zinc-500 dark:text-theme-fg-muted"
            animate={{ rotate: expandedSections.has("aesthetics") ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            expand_more
          </motion.span>
        </button>

        <AnimatePresence>
          {expandedSections.has("aesthetics") && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="space-y-4 border-t border-zinc-100 dark:border-theme-border p-4 pt-0">
                {/* Logo Upload — two slots (SGL-344).
                    Labelled by where each mark is *used*, not by what colour it is:
                    a customer knows which of their logos goes on a dark background,
                    they should not have to work out whether we mean the ink or the
                    ground. Neither is required; with only one uploaded, everything
                    behaves exactly as it did before. */}
                <LogoUpload
                  label="Logo — for light backgrounds"
                  helpText="Usually the dark-ink version. PNG or JPG up to 5MB."
                  inputId="logo-upload-light"
                  logoUrl={formData.logoUrl}
                  logoMediaId={formData.logoMediaId}
                  onLogoChange={(url, mediaId) => {
                    onFieldChange("logoUrl", url);
                    onFieldChange("logoMediaId", mediaId);
                  }}
                  error={errors.logoUrl}
                  customerId={customerId}
                />

                <LogoUpload
                  label="Logo — for dark backgrounds"
                  helpText="Usually the white-ink version. PNG or JPG up to 5MB."
                  inputId="logo-upload-dark"
                  previewOn="dark"
                  logoUrl={formData.logoUrlDark}
                  logoMediaId={formData.logoMediaIdDark}
                  onLogoChange={(url, mediaId) => {
                    onFieldChange("logoUrlDark", url);
                    onFieldChange("logoMediaIdDark", mediaId);
                  }}
                  error={errors.logoUrlDark}
                  customerId={customerId}
                />

                {/* Brand Colors */}
                <BrandColorPicker
                  colors={formData.colors}
                  onColorsChange={(colors) => onFieldChange("colors", colors)}
                  maxColors={4}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Content Guidelines Section */}
      <div className="overflow-hidden rounded-lg border border-zinc-200/90 dark:border-theme-border bg-white dark:bg-theme-surface shadow-sm ring-1 ring-zinc-200/60 dark:ring-theme-border/50">
        <button
          type="button"
          onClick={() => toggleSection("guidelines")}
          className="flex w-full items-center justify-between p-3 transition-colors hover:bg-zinc-50/80 dark:hover:bg-white/5"
        >
          <h3 className="text-base font-semibold text-brand-primary">
            Content Guidelines
          </h3>
          <motion.span
            className="material-icons text-xl text-zinc-500 dark:text-theme-fg-muted"
            animate={{ rotate: expandedSections.has("guidelines") ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            expand_more
          </motion.span>
        </button>

        <AnimatePresence>
          {expandedSections.has("guidelines") && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="space-y-4 border-t border-zinc-100 dark:border-theme-border p-4 pt-0">
                {/* Brand Voice */}
                <div>
                  <label className="block text-sm font-medium text-[#2f4941] dark:text-theme-fg mb-1.5">
                    Brand Voice <span className="text-theme-fg-muted font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={formData.brandVoice}
                    onChange={(e) =>
                      onFieldChange("brandVoice", e.target.value)
                    }
                    rows={2}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 transition resize-none ${
                      errors.brandVoice
                        ? "border-red-500 focus:ring-red-200 focus:border-red-500"
                        : "border-gray-300 focus:ring-[#2f4941]/30 focus:border-[#2f4941]"
                    }`}
                    placeholder="e.g., Friendly, direct, no emojis, short sentences..."
                  />
                  <div className="mt-1 flex items-center justify-between">
                    <p className="text-xs text-gray-500 dark:text-theme-fg-muted">
                    Describe your writing style.
                  </p>
                    {errors.brandVoice && (
                      <p className="text-xs text-red-600 flex items-center gap-1">
                        <span className="material-icons text-xs">error</span>
                        {errors.brandVoice}
                      </p>
                    )}
                  </div>
                </div>

                {/* Target Audience */}
                <div>
                  <label className="block text-sm font-medium text-[#2f4941] dark:text-theme-fg mb-1.5">
                    Target Audience <span className="text-gray-400 text-xs ml-1">(optional)</span>
                  </label>
                  <textarea
                    value={formData.targetAudience}
                    onChange={(e) =>
                      onFieldChange("targetAudience", e.target.value)
                    }
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f4941]/30 focus:border-[#2f4941] transition resize-none"
                    placeholder="e.g., Homeowners 35-65 in Seattle, busy parents..."
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-theme-fg-muted">
                    Define your ideal customers.
                  </p>
                </div>

                {/* Primary CTA - Quick Select + Custom */}
                <div>
                  <label className="block text-sm font-medium text-[#2f4941] mb-2">
                    Call-to-Actions <span className="text-gray-400 text-xs ml-1">(select or add custom)</span>
                  </label>
                  
                  {/* Quick Select Checkboxes */}
                  <div className="space-y-2 p-3 border border-gray-200 dark:border-theme-border rounded-lg bg-gray-50 dark:bg-theme-surface mb-3">
                    <p className="text-xs font-medium text-gray-600 dark:text-theme-fg mb-2">Quick Select:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {CTA_OPTIONS.map((option) => {
                        const currentCTAs = Array.isArray(formData.primaryCTA) ? formData.primaryCTA : [];
                        return (
                          <label
                            key={option.value}
                            className="flex items-center gap-2 cursor-pointer hover:bg-zinc-50 dark:hover:bg-white/5 p-2 rounded transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={currentCTAs.includes(option.label)}
                              onChange={(e) => {
                                let newCTAs: string[];
                                if (e.target.checked) {
                                  // Add if not already present (prevent duplicates)
                                  newCTAs = currentCTAs.includes(option.label)
                                    ? currentCTAs
                                    : [...currentCTAs, option.label];
                                } else {
                                  // Remove the item
                                  newCTAs = currentCTAs.filter((cta) => cta !== option.label);
                                }
                                // Remove any duplicates
                                newCTAs = Array.from(new Set(newCTAs));
                                onFieldChange("primaryCTA", newCTAs as any);
                              }}
                              className="w-4 h-4 text-[#2f4941] border-gray-300 rounded focus:ring-2 focus:ring-[#2f4941]/30"
                            />
                            <span className="text-sm text-gray-700 dark:text-theme-fg">{option.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Custom CTA Input */}
                  <ChipInput
                    label="Custom Call-to-Actions"
                    chips={Array.isArray(formData.primaryCTA) ? formData.primaryCTA : []}
                    onChipsChange={(ctas) => {
                      // Ensure we're working with an array and remove duplicates
                      const uniqueCTAs = Array.from(new Set(ctas));
                      onFieldChange("primaryCTA", uniqueCTAs as any);
                    }}
                    placeholder="Type custom CTA and press Enter"
                    helperText='Add your own CTAs like "Get a Free Estimate", "Schedule Consultation"'
                  />
                </div>

                {/* Avoid Words */}
                <ChipInput
                  label="Avoid these words/phrases (optional)"
                  chips={formData.avoidWords}
                  onChipsChange={(words) => onFieldChange("avoidWords", words)}
                  placeholder="Type a word and press Enter"
                  helperText='Prevent overused phrases like "game changer", "unlock"'
                />

                {/* Must Include Words */}
                <ChipInput
                  label="Must include phrases (optional)"
                  chips={formData.mustIncludeWords}
                  onChipsChange={(words) =>
                    onFieldChange("mustIncludeWords", words)
                  }
                  placeholder="Type a phrase and press Enter"
                  helperText='e.g., "family-owned", "free estimates"'
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
